import React, { useMemo } from 'react';
import StepShell from './StepShell';
import DeviceConnectionStatus from './DeviceConnectionStatus';
import type { ConnectionPhase } from './DeviceConnectionStatus';
import type { FlowCtx } from './types';

interface Props {
  ctx: FlowCtx;
}

function derivePhase(msg: string, popupIsOpen: boolean): ConnectionPhase {
  if (msg.includes('Device connected successfully')) return 'success';
  if (msg.includes('Device hotspot connected')) return 'connected';
  if (popupIsOpen) {
    const wasInWifiPhase = msg.includes('Sending Wi-Fi details') || msg.includes('Connecting to internet') || msg.includes('Saving device data');
    return wasInWifiPhase ? 'wifi-failed' : 'failed';
  }
  if (msg.includes('Sending Wi-Fi details') || msg.includes('Connecting to internet') || msg.includes('Saving device data')) return 'wifi-connecting';
  return 'finding';
}

const Step6SendWifi: React.FC<Props> = ({ ctx }) => {
  const phase = useMemo(() => derivePhase(ctx.statusMessage, ctx.popupIsOpen), [ctx.statusMessage, ctx.popupIsOpen]);

  return (
    <>
    <StepShell
      stepNumber={3}
      onBack={ctx.openExitModal}
      secondary={{ label: 'Back', onClick: () => ctx.goTo('wificonf') }}
      primary={{
        label: 'Go to Wi-Fi Settings',
        onClick: ctx.handleOpenSettings,
        loading: ctx.loading,
        loadingLabel: 'Connecting…',
      }}
    >
      {ctx.loading && (
        <DeviceConnectionStatus
          message={ctx.statusMessage || 'IAQ device trying connecting to WiFi network'}
          paused={ctx.popupIsOpen}
          phase={phase}
        />
      )}

      {!ctx.loading && (
        <div className="dcf-content--center">
          <h2 className="dcf-step-title">Step 3 of 4: Connect to Device Hotspot</h2>
          <div className="dcf-step-bar"></div>

          <div className="dcf-status-icon-container">
            <div className="dcf-status-icon dcf-status-icon--neutral">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12.55a11 11 0 0 1 14.08 0" strokeLinecap="round" />
                <path d="M1.42 9a16 16 0 0 1 21.16 0" strokeLinecap="round" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" strokeLinecap="round" />
                <circle cx="12" cy="20" r="1" fill="currentColor" />
              </svg>
            </div>
            <p className="dcf-status-icon-text">Connect to IAQ_ network</p>
          </div>

          <p className="dcf-step-subtitle-head">
            Couldn&apos;t auto-connect to the device
          </p>
          <p className="dcf-step-subtitle">
            Go to <strong>Settings &gt; Wi-Fi</strong> and connect to the{' '}
            <strong>IAQ_{ctx.serialNumber}</strong> network. Once connected,
            return to the app — credentials will be sent automatically.
          </p>
          <p className="dcf-step-subtitle">
            You may safely ignore the &ldquo;No Internet Connection&rdquo; warning.
          </p>
        </div>
      )}
    </StepShell>
    </>
  );
};

export default Step6SendWifi;
